import { useEffect, useMemo, useState } from "react";
import type { StoreApi } from "zustand";
import { revalidateLogic, useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  HttpError,
  retryAfterSeconds,
  useAuthStore,
  type AuthRepository,
  type AuthState,
} from "@nasam/core";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  OtpCells,
  RESEND_COOLDOWN_SECONDS,
  formatCountdown,
  useCountdown,
  withOtpVerifyHold,
} from "@nasam/ui";

import { authRepository as defaultAuthRepository } from "../../auth";
import { AuthCardLayout } from "./AuthCardLayout";
import { retryAfterSecondsFromMessage } from "./retryAfter";

// The backend's own escalating cooldown starts at 5 minutes once the
// 3-requests/hour allowance for /auth/forget-password (this resend calls
// the same endpoint) is used up -- a sane floor if a 429 ever arrives
// without a parseable Retry-After header.
const DEFAULT_RATE_LIMIT_SECONDS = 5 * 60;

export interface ResetOtpChallengeScreenProps {
  readonly authRepository?: AuthRepository;
  readonly authStore?: StoreApi<AuthState>;
}

/**
 * Step 2 of the OTP-based forgot-password flow (POST /auth/verify-reset-otp)
 * -- deliberately its own screen/state, not sharing OtpChallengeScreen's
 * pendingOtpUsername or AuthController: that machinery is login's
 * post-credentials OTP, a different backend flow entirely. The OtpCells
 * presentation is shared, and the resend cooldown starts at the same 60s as
 * OtpChallengeScreen, but resend here calls POST /auth/forget-password (the
 * same endpoint the email-entry screen uses) so a 429's real Retry-After
 * cooldown -- several minutes -- can override it.
 */
export function ResetOtpChallengeScreen({
  authRepository = defaultAuthRepository,
  authStore = useAuthStore,
}: ResetOtpChallengeScreenProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [pendingEmail, setPendingEmail] = useState(
    () => authStore.getState().pendingPasswordResetEmail,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { secondsLeft, isActive, start } = useCountdown(RESEND_COOLDOWN_SECONDS);
  const canResend = !isActive;

  useEffect(() => {
    return authStore.subscribe((state) => {
      setPendingEmail(state.pendingPasswordResetEmail);
    });
  }, [authStore]);

  useEffect(() => {
    if (authStore.getState().pendingPasswordResetEmail === null) {
      void navigate({ to: "/auth/forgot-password", replace: true });
    }
  }, [authStore, navigate]);

  const schema = useMemo(
    () =>
      z.object({
        code: z
          .string()
          .min(1, t("validation:codeRequired"))
          .regex(/^\d+$/, t("validation:codeNumeric"))
          .length(6, t("validation:codeLength")),
      }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const onSubmit = async (values: FormValues) => {
    setErrorMessage(null);
    if (pendingEmail === null) {
      void navigate({ to: "/auth/forgot-password", replace: true });
      return;
    }
    try {
      const result = await withOtpVerifyHold(
        authRepository.verifyPasswordResetOtp({
          email: pendingEmail,
          code: values.code,
        }),
      );
      authStore
        .getState()
        .setPasswordResetToken(result.resetToken, result.expiresAt);
      void navigate({ to: "/auth/reset-password" });
    } catch (err) {
      // Shown verbatim -- the backend already tells "wrong code" (attempts
      // remain), "expired", and "max attempts reached" apart, pre-translated
      // per x-lang. All three point at the same resend link below either way.
      setErrorMessage(
        err instanceof Error ? err.message : t("authentication:invalidOtp"),
      );
    }
  };

  const form = useForm({
    defaultValues: { code: "" } satisfies FormValues,
    validationLogic: revalidateLogic({ mode: "change" }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  useEffect(() => {
    if (!form.state.isValid) void form.validate("change");
  }, [i18n.language, form]);

  const handleResend = async () => {
    if (pendingEmail === null || !canResend) return;
    setErrorMessage(null);
    setResendSuccess(false);
    try {
      setResending(true);
      await authRepository.forgotPassword({ email: pendingEmail });
      setResendSuccess(true);
      start(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      if (err instanceof HttpError && err.status === 429) {
        // The resend link's own countdown already says "resend in N" -- no
        // need for a second, redundant banner repeating it.
        start(
          retryAfterSeconds(err) ??
            retryAfterSecondsFromMessage(err.message) ??
            DEFAULT_RATE_LIMIT_SECONDS,
        );
      } else {
        setErrorMessage(
          err instanceof Error ? err.message : t("common:somethingWentWrong"),
        );
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthCardLayout
      title={t("authentication:otpTitle")}
      subtitle={t("authentication:otpSubtitle", {
        destination: pendingEmail ?? "",
      })}
      illustrationSrc="/brand/otp-illustration.png"
      footer={
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <div className="flex flex-col items-center gap-4">
              <Button
                type="submit"
                form="reset-otp-challenge-form"
                variant="primary"
                className="h-13 w-full"
                disabled={isSubmitting || resending}
              >
                <span
                  key={isSubmitting ? "busy" : "idle"}
                  className="animate-in fade-in-0 duration-150"
                >
                  {isSubmitting
                    ? t("authentication:verifyingButton")
                    : t("authentication:verifyButton")}
                </span>
              </Button>

              <p className="text-body-m-r text-text">
                {`${t("authentication:otpDidNotReceive")} `}
                <button
                  type="button"
                  disabled={isSubmitting || resending || !canResend}
                  onClick={() => {
                    void handleResend();
                  }}
                  className="text-primary underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
                >
                  {resending
                    ? t("authentication:resendingOtpButton")
                    : canResend
                      ? t("authentication:resendOtpButton")
                      : t("authentication:resendOtpCountdownTime", {
                          time: formatCountdown(secondsLeft),
                        })}
                </button>
              </p>
            </div>
          )}
        </form.Subscribe>
      }
    >
      <Form
        form={form}
        id="reset-otp-challenge-form"
        className="flex w-full flex-col items-center gap-6 px-4 md:px-13 py-4"
        noValidate
      >
        {errorMessage !== null && (
          <div
            role="alert"
            className="animate-in fade-in-0 slide-in-from-top-1 w-full rounded-md border border-fail-text bg-container-default-background p-3 text-body-s-r text-fail-text"
          >
            {errorMessage}
          </div>
        )}

        {resendSuccess && (
          <div
            role="status"
            className="animate-in fade-in-0 slide-in-from-top-1 w-full rounded-md border border-container-default-border bg-container-default-background p-3 text-body-s-r text-text"
          >
            {t("authentication:otpResentSuccess")}
          </div>
        )}

        <form.Field name="code">
          {(field) => (
            <FormField field={field}>
              <FormItem className="flex w-full flex-col items-center gap-2 space-y-0">
                <FormLabel htmlFor="reset-otp-code" className="sr-only">
                  {t("authentication:otpCodeLabel")}
                </FormLabel>
                <FormControl>
                  <form.Subscribe selector={(state) => state.isSubmitting}>
                    {(isSubmitting) => (
                      <OtpCells
                        id="reset-otp-code"
                        value={field.state.value}
                        onChange={(value) => {
                          field.handleChange(value);
                          setErrorMessage(null);
                        }}
                        onBlur={field.handleBlur}
                        status={isSubmitting ? "verifying" : "idle"}
                        error={
                          !field.state.meta.isValid || errorMessage !== null
                        }
                      />
                    )}
                  </form.Subscribe>
                </FormControl>
                <FormMessage className="w-full text-center" />
              </FormItem>
            </FormField>
          )}
        </form.Field>
      </Form>
    </AuthCardLayout>
  );
}
